import { Schema } from "effect";

import {
  DashboardArtifactCell,
  DashboardDateRangePreset,
  DefinitionText,
  findDuplicate,
  Identifier,
  IsoDate,
  Label,
  NonNegativeCount,
  type ReferenceIssue,
  ShortText,
  Span,
} from "./dashboardSchemaPrimitives";
import {
  AdHocDataSource,
  METRIC_GRAIN_RANK,
  type MetricDefinition,
  type MetricDimensionType,
  type MetricFilterValue,
  MetricQuery,
} from "./metrics";

export { DashboardArtifactCell, DashboardDateRangePreset };

/**
 * Dashboard artifact specs: a declarative dashboard built from data sources
 * and widgets. The agent writes the spec, the control plane validates it and
 * resolves its data read-only, and the web app renders it with the dashboard
 * kit (`KpiCard`, `ChartPanel`, `DataTable`, filter chrome). Artifacts never
 * contain UI code.
 *
 * v1 widgets read SQL datasets. v2 widgets read either a registry metric
 * (`{ kind: "metric", query }`) or an ad-hoc SQL dataset that says what it
 * reads (`clean_tables` or `raw_replica`) and what it means in plain
 * language. Stored v1 specs keep decoding; `@t3tools/shared/dashboardSpecUpgrade`
 * turns them into v2.
 */

export const DASHBOARD_ARTIFACT_SPEC_VERSION = 1;
export const DASHBOARD_ARTIFACT_SPEC_V2_VERSION = 2;

export const DASHBOARD_ARTIFACT_LIMITS = {
  datasets: 20,
  parameters: 10,
  rows: 20,
  widgetsPerRow: 6,
  columnsPerDataset: 50,
  tableColumns: 30,
  chartSeries: 8,
  bindings: 20,
  selectOptions: 200,
  definitions: 50,
  sqlLength: 20_000,
  textLength: 4_000,
} as const;

// Parameters ---------------------------------------------------------------

/** Binds as `<id>.start` and `<id>.end`, both inclusive dates. */
export const DashboardDateRangeParameter = Schema.Struct({
  kind: Schema.Literal("date_range"),
  id: Identifier,
  label: Label,
  default: Schema.Union([
    Schema.Struct({ preset: DashboardDateRangePreset }),
    Schema.Struct({ start: IsoDate, end: IsoDate }),
  ]),
  maxDays: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 1_000 }))),
});

const SelectOption = Schema.Struct({
  value: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  label: Label,
});

export const DashboardSelectOptions = Schema.Union([
  Schema.Struct({
    from: Schema.Literal("static"),
    options: Schema.Array(SelectOption).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.selectOptions),
    ),
  }),
  /**
   * Options come from a dataset. It may bind date ranges and other selects
   * whose options are static, so one filter can scope another's choices.
   */
  Schema.Struct({
    from: Schema.Literal("dataset"),
    dataset: Identifier,
    valueColumn: Identifier,
    labelColumn: Schema.optionalKey(Identifier),
  }),
]);

/** Binds as `<id>`: text for `select`, a text array for `multi_select`. */
export const DashboardSelectParameter = Schema.Struct({
  kind: Schema.Literal("select"),
  id: Identifier,
  label: Label,
  options: DashboardSelectOptions,
  default: Schema.String,
});

export const DashboardMultiSelectParameter = Schema.Struct({
  kind: Schema.Literal("multi_select"),
  id: Identifier,
  label: Label,
  options: DashboardSelectOptions,
  default: Schema.Array(Schema.String),
});

export const DashboardParameter = Schema.Union([
  DashboardDateRangeParameter,
  DashboardSelectParameter,
  DashboardMultiSelectParameter,
]);
export type DashboardParameter = typeof DashboardParameter.Type;

// Datasets -----------------------------------------------------------------

export const DashboardColumnType = Schema.Literals([
  "number",
  "text",
  "date",
  "timestamp",
  "boolean",
]);
export type DashboardColumnType = typeof DashboardColumnType.Type;

/** Display formats for number columns. `percent` values are already 0-100. */
export const DashboardNumberFormat = Schema.Literals([
  "integer",
  "decimal",
  "percent",
  "currency_usd",
  "seconds",
]);
export type DashboardNumberFormat = typeof DashboardNumberFormat.Type;

export const DashboardDatasetColumn = Schema.Struct({
  name: Identifier,
  type: DashboardColumnType,
  format: Schema.optionalKey(DashboardNumberFormat),
  label: Schema.optionalKey(Label),
});
export type DashboardDatasetColumn = typeof DashboardDatasetColumn.Type;

const DatasetQueryFields = {
  sql: Schema.String.check(
    Schema.isMinLength(1),
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.sqlLength),
  ),
  bind: Schema.Array(Schema.String).check(Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.bindings)),
  columns: Schema.Array(DashboardDatasetColumn).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.columnsPerDataset),
  ),
};

/**
 * One read-only query. `bind` lists parameter references in placeholder order:
 * `bind[0]` is `$1`. References are a parameter id, or `<id>.start` and
 * `<id>.end` for a date range. Columns declare the result shape the widgets use.
 */
export const DashboardDataset = Schema.Struct({
  id: Identifier,
  source: Schema.Literal("analytics"),
  ...DatasetQueryFields,
});
export type DashboardDataset = typeof DashboardDataset.Type;

/**
 * A v2 ad-hoc SQL dataset. It is never a verified metric: `source` says what
 * it reads and `definition` says, in plain language, what its numbers mean.
 */
export const DashboardAdHocDataset = Schema.Struct({
  id: Identifier,
  source: AdHocDataSource,
  definition: DefinitionText,
  ...DatasetQueryFields,
});
export type DashboardAdHocDataset = typeof DashboardAdHocDataset.Type;

// Widgets ------------------------------------------------------------------

const WidgetBase = {
  id: Identifier,
  span: Schema.optionalKey(Span),
};

const KpiComparison = Schema.Struct({
  column: Identifier,
  label: Label,
  better: Schema.Literals(["higher", "lower"]),
});

export const DashboardChartSeries = Schema.Struct({
  column: Identifier,
  label: Schema.optionalKey(Label),
  /** Draw this series as a line on a bar chart, or as bars on a line chart. */
  type: Schema.optionalKey(Schema.Literals(["line", "bar"])),
  axis: Schema.optionalKey(Schema.Literals(["left", "right"])),
});

const ChartKind = Schema.Literals(["line", "area", "bar", "stacked_bar"]);
const ChartOrientation = Schema.Literals(["vertical", "horizontal"]);
const ChartSeriesList = Schema.Array(DashboardChartSeries).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.chartSeries),
);

/**
 * `label` may reference columns as `{column}`, filled from the first row; use
 * it for values that are the same on every row, such as a period start.
 */
export const DashboardTableColumn = Schema.Struct({
  column: Identifier,
  label: Schema.optionalKey(Label),
  definition: Schema.optionalKey(Identifier),
  sortable: Schema.optionalKey(Schema.Boolean),
  align: Schema.optionalKey(Schema.Literals(["left", "right"])),
});
export type DashboardTableColumn = typeof DashboardTableColumn.Type;

const TableColumnList = Schema.Array(DashboardTableColumn).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.tableColumns),
);
const TableSort = Schema.Struct({
  column: Identifier,
  direction: Schema.Literals(["asc", "desc"]),
});
const TextBody = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.textLength),
);

/** `{column}` placeholders in a KPI footnote or a table column label. */
const TEMPLATE_COLUMN = /\{([a-z][a-z0-9_]*)\}/g;

/** Distinct columns a footnote or label template references, in order. */
export function dashboardTemplateColumns(template: string | undefined): string[] {
  return [...new Set([...(template ?? "").matchAll(TEMPLATE_COLUMN)].map((match) => match[1]!))];
}

/** Replaces each `{column}` placeholder with `valueOf(column)`. */
export function fillDashboardTemplate(
  template: string,
  valueOf: (column: string) => string,
): string {
  return template.replaceAll(TEMPLATE_COLUMN, (_match, name: string) => valueOf(name));
}

/** Reads the first row. `footnote` may reference columns as `{column}`. */
const KpiFields = {
  ...WidgetBase,
  kind: Schema.Literal("kpi"),
  title: Label,
  value: Identifier,
  footnote: Schema.optionalKey(ShortText),
  comparison: Schema.optionalKey(KpiComparison),
  definition: Schema.optionalKey(Identifier),
};

/** With `groupBy`, the single series is split into one series per group value. */
const ChartFields = {
  ...WidgetBase,
  kind: Schema.Literal("chart"),
  title: Label,
  description: Schema.optionalKey(ShortText),
  chart: ChartKind,
  orientation: Schema.optionalKey(ChartOrientation),
  x: Identifier,
  series: ChartSeriesList,
  groupBy: Schema.optionalKey(Identifier),
  definition: Schema.optionalKey(Identifier),
};

const TableFields = {
  ...WidgetBase,
  kind: Schema.Literal("table"),
  title: Label,
  description: Schema.optionalKey(ShortText),
  columns: TableColumnList,
  defaultSort: Schema.optionalKey(TableSort),
  emptyMessage: Schema.optionalKey(ShortText),
  definition: Schema.optionalKey(Identifier),
};

export const DashboardKpiWidget = Schema.Struct({ ...KpiFields, dataset: Identifier });
export type DashboardKpiWidget = typeof DashboardKpiWidget.Type;

export const DashboardChartWidget = Schema.Struct({ ...ChartFields, dataset: Identifier });
export type DashboardChartWidget = typeof DashboardChartWidget.Type;

export const DashboardTableWidget = Schema.Struct({ ...TableFields, dataset: Identifier });
export type DashboardTableWidget = typeof DashboardTableWidget.Type;

/** Plain text; blank lines separate paragraphs. */
export const DashboardTextWidget = Schema.Struct({
  ...WidgetBase,
  kind: Schema.Literal("text"),
  title: Schema.optionalKey(Label),
  body: TextBody,
});
export type DashboardTextWidget = typeof DashboardTextWidget.Type;

export const DashboardWidget = Schema.Union([
  DashboardKpiWidget,
  DashboardChartWidget,
  DashboardTableWidget,
  DashboardTextWidget,
]);
export type DashboardWidget = typeof DashboardWidget.Type;

/**
 * Where a v2 data widget's rows come from. A metric query's output columns
 * are `metric`, `period`, its dimension ids, `value`, `numerator`,
 * `denominator` and `compare_value` (see `MetricQuery`); a dataset's are its
 * declared columns.
 */
export const DashboardWidgetDataSource = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("metric"), query: MetricQuery }),
  Schema.Struct({ kind: Schema.Literal("dataset"), dataset: Identifier }),
]);
export type DashboardWidgetDataSource = typeof DashboardWidgetDataSource.Type;

export const DashboardKpiWidgetV2 = Schema.Struct({
  ...KpiFields,
  data: DashboardWidgetDataSource,
});
export type DashboardKpiWidgetV2 = typeof DashboardKpiWidgetV2.Type;

export const DashboardChartWidgetV2 = Schema.Struct({
  ...ChartFields,
  data: DashboardWidgetDataSource,
});
export type DashboardChartWidgetV2 = typeof DashboardChartWidgetV2.Type;

export const DashboardTableWidgetV2 = Schema.Struct({
  ...TableFields,
  data: DashboardWidgetDataSource,
});
export type DashboardTableWidgetV2 = typeof DashboardTableWidgetV2.Type;

export const DashboardWidgetV2 = Schema.Union([
  DashboardKpiWidgetV2,
  DashboardChartWidgetV2,
  DashboardTableWidgetV2,
  DashboardTextWidget,
]);
export type DashboardWidgetV2 = typeof DashboardWidgetV2.Type;

/**
 * A partial change to a v2 widget, used by the `update_widget` edit. A key
 * applies only if the widget's kind has it; the result is validated as a
 * whole spec. `id`, `kind` and `span` are changed by other edits.
 */
export const DashboardWidgetPatch = Schema.Struct({
  title: Schema.optionalKey(Label),
  description: Schema.optionalKey(ShortText),
  data: Schema.optionalKey(DashboardWidgetDataSource),
  value: Schema.optionalKey(Identifier),
  footnote: Schema.optionalKey(ShortText),
  comparison: Schema.optionalKey(KpiComparison),
  definition: Schema.optionalKey(Identifier),
  chart: Schema.optionalKey(ChartKind),
  orientation: Schema.optionalKey(ChartOrientation),
  x: Schema.optionalKey(Identifier),
  series: Schema.optionalKey(ChartSeriesList),
  groupBy: Schema.optionalKey(Identifier),
  columns: Schema.optionalKey(TableColumnList),
  defaultSort: Schema.optionalKey(TableSort),
  emptyMessage: Schema.optionalKey(ShortText),
  body: Schema.optionalKey(TextBody),
});
export type DashboardWidgetPatch = typeof DashboardWidgetPatch.Type;

export const DashboardWidgetPatchKey = Schema.Literals([
  "title",
  "description",
  "data",
  "value",
  "footnote",
  "comparison",
  "definition",
  "chart",
  "orientation",
  "x",
  "series",
  "groupBy",
  "columns",
  "defaultSort",
  "emptyMessage",
  "body",
]);
export type DashboardWidgetPatchKey = typeof DashboardWidgetPatchKey.Type;

/** Widgets in a row share its width by `span` (equal when omitted). */
export const DashboardLayoutRow = Schema.Struct({
  title: Schema.optionalKey(Label),
  widgets: Schema.Array(DashboardWidget).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.widgetsPerRow),
  ),
});
export type DashboardLayoutRow = typeof DashboardLayoutRow.Type;

export const DashboardLayoutRowV2 = Schema.Struct({
  title: Schema.optionalKey(Label),
  widgets: Schema.Array(DashboardWidgetV2).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.widgetsPerRow),
  ),
});
export type DashboardLayoutRowV2 = typeof DashboardLayoutRowV2.Type;

/**
 * Plain-language definition, shown in "How is this calculated?". In v2 only
 * dataset widgets cite these; metric widgets take theirs from the registry.
 */
export const DashboardDefinition = Schema.Struct({
  id: Identifier,
  term: Label,
  text: DefinitionText,
  /** Key of a metric glossary entry this definition follows. */
  glossaryRef: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(200))),
});
export type DashboardDefinition = typeof DashboardDefinition.Type;

// Spec ---------------------------------------------------------------------

const SpecHeaderFields = {
  title: Label,
  description: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(1_000))),
  parameters: Schema.Array(DashboardParameter).check(
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.parameters),
  ),
};
const SpecDefinitionsField = {
  definitions: Schema.Array(DashboardDefinition).check(
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.definitions),
  ),
};

const DashboardArtifactSpecFields = Schema.Struct({
  specVersion: Schema.Literal(DASHBOARD_ARTIFACT_SPEC_VERSION),
  ...SpecHeaderFields,
  datasets: Schema.Array(DashboardDataset).check(
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.datasets),
  ),
  rows: Schema.Array(DashboardLayoutRow).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.rows),
  ),
  ...SpecDefinitionsField,
});

const DashboardArtifactSpecV2Fields = Schema.Struct({
  specVersion: Schema.Literal(DASHBOARD_ARTIFACT_SPEC_V2_VERSION),
  ...SpecHeaderFields,
  datasets: Schema.Array(DashboardAdHocDataset).check(
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.datasets),
  ),
  // A new dashboard starts with no rows, to be built into with edits.
  rows: Schema.Array(DashboardLayoutRowV2).check(
    Schema.isMaxLength(DASHBOARD_ARTIFACT_LIMITS.rows),
  ),
  ...SpecDefinitionsField,
});

type CheckedSpec =
  | typeof DashboardArtifactSpecFields.Type
  | typeof DashboardArtifactSpecV2Fields.Type;
type CheckedWidget = DashboardWidget | DashboardWidgetV2;
type CheckedDataset = DashboardDataset | DashboardAdHocDataset;
/** A widget's rows: column types (unknown without a metric catalog), and how to name them. */
type WidgetSource = {
  readonly name: string;
  readonly columns: ReadonlyMap<string, DashboardColumnType | undefined>;
};

const DIMENSION_COLUMN_TYPE: Record<MetricDimensionType, DashboardColumnType> = {
  text: "text",
  number: "number",
  boolean: "boolean",
  date: "date",
};

function filterValueMatches(type: MetricDimensionType, value: MetricFilterValue): boolean {
  if (type === "date") return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (type === "text") return typeof value === "string";
  return typeof value === type;
}

/**
 * Checks a metric query against the spec's parameters and, when given, the
 * metric catalog: known metrics, declared dimensions only, a grain at or
 * above each metric's native grain, and filter values typed by dimension.
 * Returns the query's output columns.
 */
function checkMetricQuery(
  query: MetricQuery,
  parameters: ReadonlyMap<string, DashboardParameter["kind"]>,
  catalog: ReadonlyMap<string, MetricDefinition> | undefined,
  at: (...rest: PropertyKey[]) => PropertyKey[],
): ReferenceIssue | WidgetSource {
  if ("parameter" in query.dateRange) {
    if (parameters.get(query.dateRange.parameter) !== "date_range") {
      return {
        path: at("dateRange", "parameter"),
        message: `"${query.dateRange.parameter}" is not a date range parameter`,
      };
    }
  }
  for (const [index, filter] of query.filters.entries()) {
    if (!("parameter" in filter)) continue;
    const kind = parameters.get(filter.parameter);
    if (kind !== "select" && kind !== "multi_select") {
      return {
        path: at("filters", index, "parameter"),
        message: `"${filter.parameter}" is not a select parameter`,
      };
    }
  }

  const metrics: MetricDefinition[] = [];
  if (catalog) {
    for (const [index, id] of query.metrics.entries()) {
      const metric = catalog.get(id);
      if (!metric) return { path: at("metrics", index), message: `Unknown metric "${id}"` };
      metrics.push(metric);
    }
  }
  const dimensionType = (id: string): MetricDimensionType | undefined =>
    metrics[0]?.dimensions.find((dimension) => dimension.id === id)?.type;

  for (const [index, metric] of metrics.entries()) {
    if (METRIC_GRAIN_RANK[query.grain] < METRIC_GRAIN_RANK[metric.grain]) {
      return {
        path: at("grain"),
        message: `"${metric.title}" is reported by ${metric.grain}; use ${metric.grain} or coarser`,
      };
    }
    if (metric.kind === "custom" && (query.dimensions.length > 0 || query.filters.length > 0)) {
      return {
        path: at("metrics", index),
        message: `"${metric.title}" is a custom metric and cannot be split or filtered`,
      };
    }
    const declared = new Map(metric.dimensions.map((dimension) => [dimension.id, dimension]));
    for (const [dimensionIndex, id] of query.dimensions.entries()) {
      const dimension = declared.get(id);
      if (!dimension) {
        return {
          path: at("dimensions", dimensionIndex),
          message: `"${metric.title}" has no dimension "${id}"`,
        };
      }
      if (dimension.type !== dimensionType(id)) {
        return {
          path: at("dimensions", dimensionIndex),
          message: `Dimension "${id}" has a different type across the query's metrics`,
        };
      }
    }
    for (const [filterIndex, filter] of query.filters.entries()) {
      const dimension = declared.get(filter.dimension);
      if (!dimension) {
        return {
          path: at("filters", filterIndex, "dimension"),
          message: `"${metric.title}" has no dimension "${filter.dimension}"`,
        };
      }
      if ("parameter" in filter) {
        if (dimension.type !== "text") {
          return {
            path: at("filters", filterIndex, "parameter"),
            message: `A parameter can only filter a text dimension, and "${dimension.id}" is ${dimension.type}`,
          };
        }
        continue;
      }
      const wrong = filter.values.findIndex((value) => !filterValueMatches(dimension.type, value));
      if (wrong !== -1) {
        return {
          path: at("filters", filterIndex, "values", wrong),
          message: `Filter values for "${dimension.id}" must be ${dimension.type} values`,
        };
      }
    }
  }

  const columns = new Map<string, DashboardColumnType | undefined>([["metric", "text"]]);
  if (query.grain !== "total") columns.set("period", "date");
  for (const id of query.dimensions) {
    const type = dimensionType(id);
    columns.set(id, type ? DIMENSION_COLUMN_TYPE[type] : undefined);
  }
  columns.set("value", "number");
  if (!catalog || metrics.every((metric) => metric.aggregate.kind === "ratio")) {
    columns.set("numerator", "number");
    columns.set("denominator", "number");
  }
  if (query.compareTo) columns.set("compare_value", "number");
  return { name: "the metric query", columns };
}

const METRIC_DEFINITION_MESSAGE = "Metric widgets take their definition from the metric registry";

/**
 * Checks that every id is unique and every reference resolves, for v1 and v2
 * specs alike. With a metric catalog, metric widgets are also checked against
 * their metrics' definitions.
 */
function checkReferences(
  spec: CheckedSpec,
  catalog?: ReadonlyMap<string, MetricDefinition>,
): ReferenceIssue | undefined {
  const specDatasets: ReadonlyArray<CheckedDataset> = spec.datasets;
  const datasets = new Map(specDatasets.map((dataset) => [dataset.id, dataset]));
  const definitions = new Set(spec.definitions.map((definition) => definition.id));
  const parameterKinds = new Map(
    spec.parameters.map((parameter) => [parameter.id, parameter.kind]),
  );
  const bindable = new Set<string>();
  const dateRangeRefs = new Set<string>();
  for (const parameter of spec.parameters) {
    if (parameter.kind === "date_range") {
      for (const ref of [`${parameter.id}.start`, `${parameter.id}.end`]) {
        bindable.add(ref);
        dateRangeRefs.add(ref);
      }
    } else {
      bindable.add(parameter.id);
    }
  }
  const rows: ReadonlyArray<{ readonly widgets: ReadonlyArray<CheckedWidget> }> = spec.rows;
  const widgets = rows.flatMap((row) => row.widgets);

  const duplicates: Array<[string, ReadonlyArray<string>, string]> = [
    ["parameters", spec.parameters.map((p) => p.id), "parameter"],
    ["datasets", specDatasets.map((d) => d.id), "dataset"],
    ["rows", widgets.map((w) => w.id), "widget"],
    ["definitions", spec.definitions.map((d) => d.id), "definition"],
  ];
  for (const [key, ids, noun] of duplicates) {
    const duplicate = findDuplicate(ids);
    if (duplicate) return { path: [key], message: `Duplicate ${noun} id "${duplicate}"` };
  }

  for (const [index, dataset] of specDatasets.entries()) {
    const duplicate = findDuplicate(dataset.columns.map((column) => column.name));
    if (duplicate) {
      return {
        path: ["datasets", index, "columns"],
        message: `Duplicate column "${duplicate}"`,
      };
    }
    for (const [bindIndex, ref] of dataset.bind.entries()) {
      if (!bindable.has(ref)) {
        return {
          path: ["datasets", index, "bind", bindIndex],
          message: `Unknown parameter reference "${ref}"`,
        };
      }
    }
    for (const [columnIndex, column] of dataset.columns.entries()) {
      if (column.format && column.type !== "number") {
        return {
          path: ["datasets", index, "columns", columnIndex, "format"],
          message: "Only number columns take a format",
        };
      }
    }
  }

  const columnOf = (datasetId: string, name: string) =>
    datasets.get(datasetId)?.columns.find((column) => column.name === name);

  for (const [index, parameter] of spec.parameters.entries()) {
    if (parameter.kind === "date_range") {
      if ("start" in parameter.default && parameter.default.start > parameter.default.end) {
        return {
          path: ["parameters", index, "default"],
          message: "Default start must not be after end",
        };
      }
      continue;
    }
    const { options } = parameter;
    if (options.from === "static") {
      const values = new Set(options.options.map((option) => option.value));
      const defaults = parameter.kind === "select" ? [parameter.default] : parameter.default;
      const missing = defaults.find((value) => !values.has(value));
      if (missing !== undefined) {
        return {
          path: ["parameters", index, "default"],
          message: `Default "${missing}" is not one of the options`,
        };
      }
      continue;
    }
    const source = datasets.get(options.dataset);
    if (!source) {
      return {
        path: ["parameters", index, "options", "dataset"],
        message: `Unknown dataset "${options.dataset}"`,
      };
    }
    const scopingRef = (ref: string) => {
      if (dateRangeRefs.has(ref)) return true;
      const scoping = spec.parameters.find((candidate) => candidate.id === ref);
      return (
        scoping !== undefined &&
        scoping.id !== parameter.id &&
        scoping.kind !== "date_range" &&
        scoping.options.from === "static"
      );
    };
    if (!source.bind.every(scopingRef)) {
      return {
        path: ["parameters", index, "options", "dataset"],
        message:
          "An options dataset may only bind date ranges and other selects with static options",
      };
    }
    for (const key of ["valueColumn", "labelColumn"] as const) {
      const name = options[key];
      if (name !== undefined && !columnOf(options.dataset, name)) {
        return {
          path: ["parameters", index, "options", key],
          message: `Unknown column "${name}" in dataset "${options.dataset}"`,
        };
      }
    }
  }

  for (const [rowIndex, row] of rows.entries()) {
    for (const [widgetIndex, widget] of row.widgets.entries()) {
      const at = (...rest: PropertyKey[]) => ["rows", rowIndex, "widgets", widgetIndex, ...rest];
      if (widget.kind === "text") continue;
      const data: DashboardWidgetDataSource =
        "data" in widget ? widget.data : { kind: "dataset", dataset: widget.dataset };
      const isMetric = data.kind === "metric";
      if (widget.definition !== undefined) {
        if (isMetric) return { path: at("definition"), message: METRIC_DEFINITION_MESSAGE };
        if (!definitions.has(widget.definition)) {
          return { path: at("definition"), message: `Unknown definition "${widget.definition}"` };
        }
      }

      let source: WidgetSource;
      if (data.kind === "metric") {
        const checked = checkMetricQuery(data.query, parameterKinds, catalog, (...rest) =>
          at("data", "query", ...rest),
        );
        if ("message" in checked) return checked;
        source = checked;
      } else {
        const dataset = datasets.get(data.dataset);
        if (!dataset) {
          return {
            path: "data" in widget ? at("data", "dataset") : at("dataset"),
            message: `Unknown dataset "${data.dataset}"`,
          };
        }
        source = {
          name: `"${data.dataset}"`,
          columns: new Map(dataset.columns.map((column) => [column.name, column.type])),
        };
      }
      const isNotNumber = (name: string) => {
        const type = source.columns.get(name);
        return type !== undefined && type !== "number";
      };
      const requireColumn = (name: string, path: PropertyKey[]): ReferenceIssue | undefined =>
        source.columns.has(name)
          ? undefined
          : { path: at(...path), message: `Unknown column "${name}" in ${source.name}` };

      if (widget.kind === "kpi") {
        const issue =
          requireColumn(widget.value, ["value"]) ??
          (widget.comparison
            ? requireColumn(widget.comparison.column, ["comparison", "column"])
            : undefined);
        if (issue) return issue;
        if (widget.comparison && [widget.value, widget.comparison.column].some(isNotNumber)) {
          return {
            path: at("comparison", "column"),
            message: "A comparison needs number value and comparison columns",
          };
        }
        for (const name of dashboardTemplateColumns(widget.footnote)) {
          const footnoteIssue = requireColumn(name, ["footnote"]);
          if (footnoteIssue) return footnoteIssue;
        }
      } else if (widget.kind === "chart") {
        const issue =
          requireColumn(widget.x, ["x"]) ??
          (widget.groupBy ? requireColumn(widget.groupBy, ["groupBy"]) : undefined);
        if (issue) return issue;
        if (widget.groupBy && widget.series.length !== 1) {
          return { path: at("series"), message: "A grouped chart takes exactly one series" };
        }
        if (widget.orientation === "horizontal" && widget.chart !== "bar") {
          return { path: at("orientation"), message: "Only bar charts can be horizontal" };
        }
        for (const [seriesIndex, series] of widget.series.entries()) {
          const seriesIssue = requireColumn(series.column, ["series", seriesIndex, "column"]);
          if (seriesIssue) return seriesIssue;
          if (isNotNumber(series.column)) {
            return {
              path: at("series", seriesIndex, "column"),
              message: `Series column "${series.column}" must be a number column`,
            };
          }
        }
      } else {
        for (const [columnIndex, column] of widget.columns.entries()) {
          const issue = requireColumn(column.column, ["columns", columnIndex, "column"]);
          if (issue) return issue;
          for (const name of dashboardTemplateColumns(column.label)) {
            const labelIssue = requireColumn(name, ["columns", columnIndex, "label"]);
            if (labelIssue) return labelIssue;
          }
          if (column.definition !== undefined) {
            const path = at("columns", columnIndex, "definition");
            if (isMetric) return { path, message: METRIC_DEFINITION_MESSAGE };
            if (!definitions.has(column.definition)) {
              return { path, message: `Unknown definition "${column.definition}"` };
            }
          }
        }
        if (widget.defaultSort) {
          const sortColumn = widget.defaultSort.column;
          if (!widget.columns.some((column) => column.column === sortColumn)) {
            return {
              path: at("defaultSort", "column"),
              message: `Sort column "${sortColumn}" is not a table column`,
            };
          }
        }
      }
    }
  }
  return undefined;
}

export const DashboardArtifactSpec = DashboardArtifactSpecFields.check(
  Schema.makeFilter((spec) => checkReferences(spec), {
    identifier: "DashboardArtifactSpec",
  }),
);
export type DashboardArtifactSpec = typeof DashboardArtifactSpec.Type;

/**
 * Spec v2, checked without the metric catalog: metric widgets are checked for
 * parameter references and output columns, not against metric definitions.
 * Use `dashboardArtifactSpecV2For(metrics)` where the catalog is known.
 */
export const DashboardArtifactSpecV2 = DashboardArtifactSpecV2Fields.check(
  Schema.makeFilter((spec) => checkReferences(spec), {
    identifier: "DashboardArtifactSpecV2",
  }),
);
export type DashboardArtifactSpecV2 = typeof DashboardArtifactSpecV2.Type;

/**
 * Spec v2 checked against a metric catalog too: known metrics, declared
 * dimensions only, a grain at or above native, and typed filter values.
 */
export function dashboardArtifactSpecV2For(metrics: ReadonlyArray<MetricDefinition>) {
  const catalog = new Map(metrics.map((metric) => [metric.id, metric]));
  return DashboardArtifactSpecV2Fields.check(
    Schema.makeFilter((spec) => checkReferences(spec, catalog), {
      identifier: "DashboardArtifactSpecV2",
    }),
  );
}

/** Any stored spec version. Upgrade v1 with `@t3tools/shared/dashboardSpecUpgrade`. */
export const AnyDashboardArtifactSpec = Schema.Union([
  DashboardArtifactSpec,
  DashboardArtifactSpecV2,
]);
export type AnyDashboardArtifactSpec = typeof AnyDashboardArtifactSpec.Type;

// Resolved data ------------------------------------------------------------

const ResolvedValue = Schema.Struct({ column: Identifier, value: DashboardArtifactCell });
const ResolvedNumbers = Schema.Array(Schema.NullOr(Schema.Finite));

/**
 * A KPI's values from the first row; `hasRow` is false when the data is
 * empty. `suppressed` is true when the value is hidden because an underlying
 * count is under 11.
 */
export const ResolvedKpiWidget = Schema.Struct({
  kind: Schema.Literal("kpi"),
  widgetId: Identifier,
  hasRow: Schema.Boolean,
  value: ResolvedValue,
  comparison: Schema.optionalKey(
    Schema.Struct({ column: Identifier, value: Schema.NullOr(Schema.Finite) }),
  ),
  footnote: Schema.Array(ResolvedValue),
  suppressed: Schema.optionalKey(Schema.Boolean),
});
export type ResolvedKpiWidget = typeof ResolvedKpiWidget.Type;

export const ResolvedChartSeries = Schema.Struct({
  column: Identifier,
  /** The spec's series label; grouped series are named by `group` instead. */
  label: Schema.optionalKey(Label),
  group: Schema.optionalKey(DashboardArtifactCell),
  type: Schema.Literals(["line", "bar"]),
  axis: Schema.Literals(["left", "right"]),
  values: ResolvedNumbers,
  /** Indexes of x values whose value is hidden because a count is under 11. */
  suppressed: Schema.optionalKey(Schema.Array(NonNegativeCount)),
});
export type ResolvedChartSeries = typeof ResolvedChartSeries.Type;

/** Distinct x values in row order, with one value per x in every series. */
export const ResolvedChartWidget = Schema.Struct({
  kind: Schema.Literal("chart"),
  widgetId: Identifier,
  x: Schema.Struct({ column: Identifier, values: Schema.Array(DashboardArtifactCell) }),
  series: Schema.Array(ResolvedChartSeries),
}).check(
  Schema.makeFilter(
    (chart) =>
      chart.series.every((series) => series.values.length === chart.x.values.length) ||
      "Every series needs one value per x value",
    { identifier: "ResolvedChartWidget" },
  ),
);
export type ResolvedChartWidget = typeof ResolvedChartWidget.Type;

/**
 * Rows projected to the table's columns, in column order. `labelValues` holds
 * the first row's values for columns that header labels reference.
 */
export const ResolvedTableWidget = Schema.Struct({
  kind: Schema.Literal("table"),
  widgetId: Identifier,
  columns: Schema.Array(Identifier),
  rows: Schema.Array(Schema.Array(DashboardArtifactCell)),
  labelValues: Schema.optionalKey(Schema.Array(ResolvedValue)),
  /** Cells hidden because a count is under 11, by row and column index. */
  suppressed: Schema.optionalKey(
    Schema.Array(Schema.Struct({ row: NonNegativeCount, column: NonNegativeCount })),
  ),
}).check(
  Schema.makeFilter(
    (table) =>
      table.rows.every((row) => row.length === table.columns.length) ||
      "Every row needs one value per column",
    { identifier: "ResolvedTableWidget" },
  ),
);
export type ResolvedTableWidget = typeof ResolvedTableWidget.Type;

/** A widget whose data could not be resolved; the rest of the dashboard still renders. */
export const ResolvedWidgetError = Schema.Struct({
  kind: Schema.Literal("error"),
  widgetId: Identifier,
  message: Schema.String,
});
export type ResolvedWidgetError = typeof ResolvedWidgetError.Type;

/** What a data widget draws, derived from its data source's rows by the control plane. */
export const ResolvedDashboardWidget = Schema.Union([
  ResolvedKpiWidget,
  ResolvedChartWidget,
  ResolvedTableWidget,
  ResolvedWidgetError,
]);
export type ResolvedDashboardWidget = typeof ResolvedDashboardWidget.Type;
